import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import ClearAll from "./ClearAll";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import { useSelector } from "react-redux";
import {
  OwcIconButton,
  OwcTooltip,
  OwcTypography,
  OwcIcon,
  OwcListItem,
  OwcSelectDropdown,
  OwcEditableDropdown
} from "@one/react";
import {
  ALL_EQUIPMENT,
  FORCE_TIMELINE_RANGE_KEY,
  LIST_GROUP_BY_BOOKING,
  LIST_GROUP_BY_EQUIPMENT,
  LIST_GROUP_BY_NONE,
  LIST_VIEW,
  MY_LAB,
  SHOW_ADVANCE_FILTER_TOOLTIP,
  TIMELINE_VIEW,
} from "../../constants";
import Search from "./Search";
import moment from "moment-timezone";
import BookingViewChanger from "../booking-calendars/booking-view-changer/BookingViewChanger";
import { GoogleAuth } from "../../components/shared/GoogleAuth";
import { withApollo } from "react-apollo";
import { getAllData } from "../../utils/helpers/fetching";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../gql/bookingapi";
import { IconButton } from "@mui/material";
import ColumnsOutlinedGrey from "../../components/EquipmentList/icons/ColumnsOutlinedGrey";
import ColumnsOutlinedActive from "../../components/EquipmentList/icons/ColumnsOutlinedActive";
import FilterOutlinedGrey from "../../components/EquipmentList/icons/FilterOutlinedGrey";
import FilterOutlinedActive from "../../components/EquipmentList/icons/FilterOutlinedActive";
import { TOOL_PANELS } from "../../components/EquipmentList/Constants/constants";
import { get4WeekStartAndEndInISOString } from "../../utils/helpers/text";
import { handleOnCellClick } from "../booking-calendars/helpers";
import { computeIsResetDisabled } from "../bookings/BookingFilter.utils";
import styles from "./BookingFilter.module.scss";

export const formatDateRange = (range) => {
  if (!range || range.length !== 2) return '';
  const [from, to] = range;
  return `${moment(from).format('DD MMM YYYY')} – ${moment(to).format('DD MMM YYYY')}`;
};

export const getMyBookingsFilterVisibility = (view, listGroupByView) => {
  const isListGroupedByBookingInList = view === LIST_VIEW && listGroupByView === LIST_GROUP_BY_BOOKING;
  const isListGroupedByNoneInList = view === LIST_VIEW && (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT);
  const isListView = isListGroupedByBookingInList || isListGroupedByNoneInList;
  const isTimelineView = view === TIMELINE_VIEW;

  return {
    isListView,
    isTimelineView,
    isGroupedByBookingInList: isListGroupedByBookingInList,
    isGroupedByNoneInList: isListGroupedByNoneInList,
    showAdvancedFilterButton: isListGroupedByNoneInList || isTimelineView,
    showPredefinedFilter: !isListGroupedByBookingInList || isTimelineView,
    showClearAllInline: isListGroupedByNoneInList || isTimelineView,
    showListSidebarActions: isListGroupedByNoneInList,
  };
};

export const getDefaultSavedFilterName = (savedFilterOptions) => {
  if (!savedFilterOptions?.length) {
    return "";
  }

  const myLabOption = savedFilterOptions.find((option) => option.name === MY_LAB);
  return myLabOption ? MY_LAB : ALL_EQUIPMENT;
};

export const shouldSkipSavedFilterChange = (selectedFilterName, signeeFilter) => {
  const isEmptySelection = selectedFilterName === "";
  const isSigneeEmpty = !signeeFilter || signeeFilter === "" || signeeFilter === "All users";
  return isEmptySelection && isSigneeEmpty;
};

const BookingFilterStyled = styled.div`
  display: flex;
  align-items: start;
  padding-bottom: 10px;
  padding-left: 10px;
  flex-wrap: wrap;
  gap: 10px
`;

export const togglePanel = ({
  panel,
  showSideBar,
  isPanelActive,
  setShowSideBar,
  setCurrentSideBarPanel,
  setPanelActive,
  setOtherPanelActive,
}) => {
  setOtherPanelActive(false);

  if (!showSideBar) {
    setCurrentSideBarPanel(panel);
    setPanelActive(true);
    setShowSideBar(true);
    return;
  }

  setShowSideBar(false);
  setCurrentSideBarPanel(panel);

  if (!isPanelActive) {
    setTimeout(() => {
      setShowSideBar(true);
    });
  }

  setPanelActive((prevState) => !prevState);
};

const FilterSelectionRow = ({ model }) => {
  const {
    showFilterSection,
    showPredefinedFilter,
    isGroupedByBookingInList,
    isGroupView,
    savedFilterOptions,
    safeSavedFilterOptions,
    savedFilter,
    handleSavedFilterChange,
    showListSidebarActions,
    showSideBar,
    isFilterActive,
    setShowSideBar,
    setCurrentSideBarPanel,
    setIsFilterActive,
    setIsColumnActive,
  } = model;

  return (
    <div style={{ display: showFilterSection ? "block" : "none" }}>
    <div style={{ display: "flex" }}>
      {showPredefinedFilter && (
        <OwcSelectDropdown
          label="Select predefined filter"
          disabled={isGroupedByBookingInList || isGroupView}
          style={{ width: 200, marginLeft: 8, marginRight: 8 }}
          onSelectChange={(e) => {
            if (isGroupView) return;
            const opt = savedFilterOptions[e.detail[0]];
            if (opt) {
              handleSavedFilterChange(opt.name);
            }
          }}
        >
          {safeSavedFilterOptions.map((o) => (
            <OwcListItem key={o.name} value={o.name} selected={savedFilter === o.name}>
              {o.label}
            </OwcListItem>
          ))}
        </OwcSelectDropdown>
      )}

      {showListSidebarActions && (
        <IconButton
          id="filter-list"
          onClick={() =>
            togglePanel({
              panel: TOOL_PANELS.filters,
              showSideBar,
              isPanelActive: isFilterActive,
              setShowSideBar,
              setCurrentSideBarPanel,
              setPanelActive: setIsFilterActive,
              setOtherPanelActive: setIsColumnActive,
            })
          }
        >
          {isFilterActive ? <FilterOutlinedActive /> : <FilterOutlinedGrey />}
        </IconButton>
      )}
    </div>
    </div>
  );
};

const ColumnSelectionRow = ({ model }) => {
  const {
    showColumnsSection,
    displayPreset,
    displayPresetOptions,
    handleDisplayPresetChange,
    isGroupedByBookingInList,
    showListSidebarActions,
    showSideBar,
    isColumnActive,
    setShowSideBar,
    setCurrentSideBarPanel,
    setIsColumnActive,
    setIsFilterActive,
  } = model;

  return (
    <div style={{ display: showColumnsSection ? "flex" : "none" }}>
    <OwcEditableDropdown
      key={displayPreset?.name}
      label="Predefined columns"
      disabled={isGroupedByBookingInList}
      style={{ width: 200, marginRight: 8 }}
      onSelectChange={(ev) => {
        const selected = displayPresetOptions[ev.detail];
        if (selected) {
          handleDisplayPresetChange(selected);
        } else {
          handleDisplayPresetChange(null);
        }
      }}
    >
      {displayPresetOptions.map((o) => (
        <OwcListItem key={o.name} value={o.name} selected={displayPreset?.name === o.name}>
          {o.label}
        </OwcListItem>
      ))}
    </OwcEditableDropdown>

    {showListSidebarActions && (
      <IconButton
        id="column-filter"
        onClick={() =>
          togglePanel({
            panel: TOOL_PANELS.columns,
            showSideBar,
            isPanelActive: isColumnActive,
            setShowSideBar,
            setCurrentSideBarPanel,
            setPanelActive: setIsColumnActive,
            setOtherPanelActive: setIsFilterActive,
          })
        }
      >
        {isColumnActive ? <ColumnsOutlinedActive /> : <ColumnsOutlinedGrey />}
      </IconButton>
    )}
    </div>
  );
};

const AdvancedFiltersSection = ({ model }) => {
  const {
    isListView,
    isGroupedByBookingInList,
    isTimelineView,
    showClearAllInline,
    handleReset,
    isResetDisabled,
  } = model;

  const showFilterSection = isListView || isTimelineView;
  const showColumnsSection = isListView && !isGroupedByBookingInList;

  const sectionModel = {
    ...model,
    showFilterSection,
    showColumnsSection,
  };

  return (
    <>
      <FilterSelectionRow model={sectionModel} />
      <ColumnSelectionRow model={sectionModel} />

      <div style={{ display: showClearAllInline ? "block" : "none", margin: "auto 0" }}>
        <ClearAll onClickCallback={handleReset} disabled={isResetDisabled} />
      </div>
    </>
  );
};

export const BookingFilterControls = ({ model, ...legacyProps }) => {
  const controlsModel = model || legacyProps;

  const {
    isListView,
    isGroupedByBookingInList,
    showAdvancedFilterButton,
    selectedDateRange,
    dispatchAction,
    setTempRowData,
    setDisableResetAll,
    setSelectedDateRange,
    dataTimeZone,
    displayTimeZone,
    handleReset,
    isResetDisabled,
    setShowAdvanceFilter,
    showAdvanceFilter,
    setSearchQuery,
    fetching,
    searchQuery,
  } = controlsModel;

  return (
    <BookingFilterStyled data-testid="booking-filter-admin">
      <div style={{ display: isListView ? "block" : "none" }}>
        <Search
          onChange={(e) => setSearchQuery(e.detail)}
          onClear={() => setSearchQuery("")}
          label="Search all columns"
          placeholder="Search all columns"
          testid={"common-search-drop-booking"}
          fetching={fetching}
          compact={false}
          searchQuery={searchQuery}
        ></Search>
      </div>

      {isListView && (
        <div style={{ padding: "0 5px 0 5px" }} className="date-picker-my-booking">
          <Datepicker
            id="datepicker-admin-panel"
            controls={["calendar"]}
            value={selectedDateRange}
            inputStyle="box"
            style={{ height: "5px" }}
            select="range"
            display="anchored"
            inRangeInvalid={true}
            rangeEndInvalid={true}
            startIcon={<OwcIcon name="date" type="outlined" />}
            onTempChange={handleOnCellClick}
            headerText="Experiment date(range)"
            label={"Date range"}
            touchUi={"auto"}
            dateFormat="D-MMM-YYYY"
            onClose={(param) => {
              if (param?.value) {
                dispatchAction({
                  type: "clearAll",
                });
                setTempRowData([]);
                setDisableResetAll(false);
                setSelectedDateRange(param?.value);
              }
            }}
            timezonePlugin={momentTimezone}
            dataTimezone={dataTimeZone}
            displayTimezone={displayTimeZone}
            buttons={["cancel", "set"]}
            compact
            exclusiveEndDates={false}
          />
        </div>
      )}

      <div style={{ display: isGroupedByBookingInList ? "block" : "none", margin: "auto 0" }}>
        <ClearAll onClickCallback={handleReset} disabled={isResetDisabled} />
      </div>

      <div style={{ display: showAdvancedFilterButton ? "block" : "none", margin: "auto 0" }}>
        <OwcTooltip anchor={"advance-filter"} placement="top">
          {SHOW_ADVANCE_FILTER_TOOLTIP}
        </OwcTooltip>
        <OwcIconButton
          id="advance-filter"
          data-testid="advance-filter-button"
          style={{ marginRight: 8 }}
          icon=" parameter_set"
          type="filled"
          onClick={() => {
            setShowAdvanceFilter((prev) => !prev);
          }}
          flat
        />
      </div>

      {showAdvanceFilter && (
        <AdvancedFiltersSection model={controlsModel} />
      )}
    </BookingFilterStyled>
  );
};


export const BookingFilter = ({
  setResetAll,
  showAdvanceFilter,
  setShowAdvanceFilter,
  selectedDateRange,
  setSelectedDateRange,
  disableResetAll,
  setDisableResetAll,
  searchQuery,
  setSearchQuery,
  setTempRowData,
  view,
  setView,
  client,
  savedFilter,
  setSavedFilter,
  displayPreset,
  setDisplayPreset,
  setInnerTableFiltersByList,
  setListGroupByView,
  listGroupByView,
  isEquipmentView,
  setIsEquipmentView,
  tempRowData,
  signeeFilter,
  setSigneeFilter,
  showFilters = true,
  setShowFilters,
  groupName,
  isGroupView = false,
  showInvalidFilter = false,
  userFilters,
  userDisplayColumnMaster,
  myLabFilter, currentSideBarPanel, setCurrentSideBarPanel, setShowSideBar, showSideBar,
  isFilterActive, setIsFilterActive, isColumnActive, setIsColumnActive
}) => {
  const {
    isListView,
    isTimelineView,
    isGroupedByBookingInList,
    showAdvancedFilterButton,
    showPredefinedFilter,
    showClearAllInline,
    showListSidebarActions,
  } = getMyBookingsFilterVisibility(view, listGroupByView);

  momentTimezone.moment = moment;
  const user = useSelector((state) => state.user);
  const { fetching, dispatchAction } = useContext(PaginationContext);
  const dataTimeZone = "utc";
  const displayTimeZone = user.timezone;
  const [allUsers, setAllUsers] = useState([]);

  const displayPresetOptions = useMemo(() => {
    return (userDisplayColumnMaster || [])
      .map((p) => ({ name: p.displayName + p.userID, label: p.displayName, definition: p.displayDefinition }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  }, [userDisplayColumnMaster]);

  const handleDisplayPresetChange = useCallback((selected) => {
    setDisplayPreset(selected); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savedFilterOptions = useMemo(() => {
    const myLab = [];
    const allEquipment = [{ name: ALL_EQUIPMENT, label: "All Equipment" }];
    const rest = [];

    if (myLabFilter) {
      myLab.push({ name: MY_LAB, label: "My Lab" });
    }

    userFilters.forEach(filter => {
      if (filter.filterName && filter.filterName !== MY_LAB) {
        rest.push({ name: filter.filterName, label: filter.filterName });
      }
    });

    rest.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
    );

    return [...myLab, ...allEquipment, ...rest];
  }, [myLabFilter, userFilters]);

  const safeSavedFilterOptions = savedFilterOptions || [];

  const loadUsers = useCallback(async () => {
    try {
      const { items } = await getAllData({
        client,
        query: LIST_DIGITAL_LAB_BOOKING_USERS,
        dataPath: ["data", "listDigitalLabBookingUsers"],
        variables: { limit: 1000 },
        cache: true,
      });

      setAllUsers(items || []);
    } catch (error) {
      console.error("Error loading users:", error);
    }
  }, [client]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if ((savedFilter && savedFilter !== '__ALL_EQUIPMENT__') || displayPreset || searchQuery !== '') {
      setDisableResetAll(false);
    }
  }, [savedFilter, setDisableResetAll, displayPreset, searchQuery]);

  useEffect(() => {
    if (!savedFilter && savedFilterOptions.length > 0) {
      const myLabOption = savedFilterOptions.find(option => option.name === MY_LAB);
      if (myLabOption) {
        setSavedFilter(MY_LAB);
      } else {
        setSavedFilter(savedFilterOptions[0].name);
      }
    }
  }, [myLabFilter, userFilters, allUsers, savedFilter, savedFilterOptions, setSavedFilter, setSigneeFilter]);

  useEffect(() => {
    const raw = sessionStorage.getItem(FORCE_TIMELINE_RANGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const dateFrom = parsed?.dateFrom;
      const dateTo = parsed?.dateTo;
      if (!dateFrom || !dateTo) return;
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return;
      dispatchAction({ type: "clearAll" });
      setTempRowData([]);
      setDisableResetAll(false);
      setSelectedDateRange([from, to]);
      setResetAll?.(prev => !prev);
    } catch (e) {
    } finally {
      sessionStorage.removeItem(FORCE_TIMELINE_RANGE_KEY);
    }// eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSavedFilterChange = useCallback((selectedFilterName) => {
    if (shouldSkipSavedFilterChange(selectedFilterName, signeeFilter)) {
      return;
    }
    if (!isGroupView) {
      setSavedFilter(selectedFilterName);
    }

    dispatchAction({ type: "clearAll" });
    setTempRowData([]);
    setDisableResetAll(false); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signeeFilter, user.user, allUsers, setSavedFilter, dispatchAction, setTempRowData, setDisableResetAll, isGroupView]);

  const setDefaultTimeRange = useCallback(() => {
    setSelectedDateRange([
      get4WeekStartAndEndInISOString("start", user.timezone),
      get4WeekStartAndEndInISOString("end", user.timezone),
    ]);
  }, [setSelectedDateRange, user.timezone]);

  const getDefaultTimeRange = useCallback(() => {
    return [
      get4WeekStartAndEndInISOString("start", user.timezone),
      get4WeekStartAndEndInISOString("end", user.timezone),
    ];
  }, [user.timezone]);

  const handleReset = useCallback(() => {
    const defaultSavedFilter = getDefaultSavedFilterName(savedFilterOptions);
    if (defaultSavedFilter) {
      setSavedFilter(defaultSavedFilter);
    }

    setDefaultTimeRange();
    setSearchQuery("");
    setSigneeFilter("");
    setResetAll(prev => !prev);
    setTempRowData([]);
    setInnerTableFiltersByList({});
    handleDisplayPresetChange(null)
    setDisableResetAll(true);

    dispatchAction({ type: "clearAll" }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterOptions, setSavedFilter, setDefaultTimeRange, setSigneeFilter, setSearchQuery, setResetAll, setTempRowData, setInnerTableFiltersByList, setDisableResetAll, setDisplayPreset, dispatchAction]);

  // Add effect to handle view switching
  useEffect(() => {
    // Clear temporary data when switching views to prevent stale data

    setTempRowData([]);
    dispatchAction({ type: "clearAll" });
  }, [view, isEquipmentView, setTempRowData, dispatchAction]);


  // Add effect to handle groupName changes in group view
  useEffect(() => {
    if (isGroupView && groupName && groupName !== savedFilter && savedFilterOptions.find((option) => option.name === groupName)) {
      setSavedFilter(groupName);
      // Clear temporary data when group changes
      setTempRowData([]);
      dispatchAction({ type: "clearAll" });
      setDisableResetAll(false);
    }
  }, [groupName, isGroupView, savedFilter, setSavedFilter, setTempRowData, dispatchAction, setDisableResetAll, savedFilterOptions]);

  const isResetDisabled = useMemo(() => {
    const defaultSavedFilter = getDefaultSavedFilterName(savedFilterOptions);

    return computeIsResetDisabled({
      savedFilterOptions,
      enableResetAll: false,
      signeeFilter,
      savedFilter: isGroupView ? defaultSavedFilter : savedFilter,
      searchQuery,
      displayPreset,
      isGroupView,
      selectedDateRange,
      getDefaultDateRange: getDefaultTimeRange,
    });
  }, [savedFilterOptions, signeeFilter, savedFilter, searchQuery, displayPreset, isGroupView, selectedDateRange, getDefaultTimeRange]);

  const filterControlsModel = {
    isGroupView,
    isListView,
    isTimelineView,
    isGroupedByBookingInList,
    showAdvancedFilterButton,
    showPredefinedFilter,
    showClearAllInline,
    showListSidebarActions,
    selectedDateRange,
    dispatchAction,
    setTempRowData,
    setDisableResetAll,
    setSelectedDateRange,
    dataTimeZone,
    displayTimeZone,
    handleReset,
    isResetDisabled,
    setShowAdvanceFilter,
    showAdvanceFilter,
    savedFilterOptions,
    safeSavedFilterOptions,
    savedFilter,
    handleSavedFilterChange,
    displayPreset,
    displayPresetOptions,
    handleDisplayPresetChange,
    isFilterActive,
    setIsFilterActive,
    isColumnActive,
    setIsColumnActive,
    showSideBar,
    setShowSideBar,
    setCurrentSideBarPanel,
    setSearchQuery,
    fetching,
    searchQuery,
  };

  return (
    <>
      <div style={{ padding: 15, display: "flex", justifyContent: "space-between", gap: '10px' }}>
        <OwcTypography className={styles.pageTitle} variant="button">{`My Bookings`}</OwcTypography>
        <div style={{ marginLeft: "auto 0" }}>
          <GoogleAuth />
        </div>
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >

        <>
          <BookingFilterControls model={filterControlsModel} />

          <BookingViewChanger setTempRowData={setTempRowData} myBookingsView view={view} setView={setView} setListGroupByView={setListGroupByView} listGroupByView={listGroupByView} isEquipmentView={isEquipmentView} setIsEquipmentView={setIsEquipmentView} />
        </>


      </div>
    </>
  );
};

export default withApollo(BookingFilter);
